import React, { useState, useEffect } from 'react';
import { Vote, Send, Award, CheckCircle2, AlertCircle, User, Mail, MessageSquare, Code, Star, X, ArrowLeft, BookOpen as BookOpenIcon } from 'lucide-react';
import API_BASE_URL from '../Config/config';

const TechnologyVotingForm = ({ isDark, onClose }) => {
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    current_learning: '',
    requested_technology: '',
    reason: '',
    experience_level: 'Beginner',
    additional_comments: ''
  });
  
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    // Prefill user data if available
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    if (user && Object.keys(user).length > 0) {
      setFormData(prev => ({
        ...prev,
        full_name: user.name || (user.first_name && user.last_name ? user.first_name + ' ' + user.last_name : user.userId || user.id || ''),
        email: user.email || ''
      }));
    }
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
    const userId = localStorage.getItem('user_id') || storedUser.userId || storedUser.id;

    if (!userId) {
      setMessage({ type: 'error', text: 'Please login to submit' });
      return;
    }

    setSubmitting(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/progress/vote`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'user-id': userId
        },
        body: JSON.stringify({ ...formData, user_id: userId })
      });
      const data = await response.json();
      if (data.success) {
        localStorage.setItem('has_submitted_suggestion', 'true');
        setMessage({ type: 'success', text: 'Thank you for your suggestion! We will review it shortly.' });
        setTimeout(() => {
            if (onClose) onClose();
        }, 3000);
      } else {
        setMessage({ type: 'error', text: data.error || 'Failed to submit' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Connection error. Please try again.' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={`fixed inset-0 z-[110] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm`}>
      <div className={`relative w-full max-w-2xl p-8 rounded-3xl shadow-2xl border animate-in zoom-in-95 duration-300 overflow-y-auto max-h-[90vh] ${
        isDark ? 'bg-[#111827] border-white/10 text-white' : 'bg-white border-gray-100 text-gray-900'
      }`}>
        <button onClick={onClose} className="absolute top-6 right-6 p-2 hover:bg-white/5 rounded-full transition-colors">
            <X className="w-6 h-6 text-gray-400" />
        </button>

        <div className="flex items-center gap-4 mb-8">
            <button 
                onClick={onClose}
                className={`p-2 rounded-xl transition-colors md:hidden ${isDark ? 'hover:bg-white/10 text-white' : 'hover:bg-gray-100 text-gray-700'}`}
            >
                <ArrowLeft className="w-6 h-6" />
            </button>
            <div className="p-3 bg-purple-600 rounded-2xl shadow-lg shadow-purple-600/20">
                <Vote className="w-8 h-8 text-white" />
            </div>
            <div>
                <h3 className="text-2xl font-black uppercase tracking-tight">Suggest <span className="text-purple-500">Technology</span></h3>
                <p className="text-xs font-bold text-gray-500 uppercase tracking-widest mt-1">Shape the future of SkillGen Genie</p>
            </div>
        </div>

        {message.text && (
          <div className={`mb-8 p-4 rounded-2xl flex items-center gap-3 animate-in fade-in slide-in-from-top-4 ${
            message.type === 'success' ? 'bg-green-500/10 text-green-400 border border-green-500/20' : 'bg-red-500/10 text-red-400 border border-red-500/20'
          }`}>
            {message.type === 'success' ? <CheckCircle2 className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />}
            <p className="text-sm font-bold">{message.text}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
                <label className="text-[10px] font-black uppercase tracking-widest text-gray-500 flex items-center gap-2">
                    <User className="w-3 h-3" /> Full Name
                </label>
                <input
                    required
                    name="full_name"
                    value={formData.full_name}
                    onChange={handleChange}
                    className={`w-full p-4 rounded-xl border outline-none transition-all ${isDark ? 'bg-white/5 border-white/10 focus:border-purple-500' : 'bg-gray-50 border-gray-200 focus:border-purple-500'}`}
                    placeholder="John Doe"
                />
            </div>
            <div className="space-y-2">
                <label className="text-[10px] font-black uppercase tracking-widest text-gray-500 flex items-center gap-2">
                    <Mail className="w-3 h-3" /> Email Address
                </label>
                <input
                    required
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className={`w-full p-4 rounded-xl border outline-none transition-all ${isDark ? 'bg-white/5 border-white/10 focus:border-purple-500' : 'bg-gray-50 border-gray-200 focus:border-purple-500'}`}
                    placeholder="john@example.com"
                />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
                <label className="text-[10px] font-black uppercase tracking-widest text-gray-500 flex items-center gap-2">
                    <BookOpenIcon className="w-3 h-3" /> Current Learning
                </label>
                <input
                    name="current_learning"
                    value={formData.current_learning}
                    onChange={handleChange}
                    className={`w-full p-4 rounded-xl border outline-none transition-all ${isDark ? 'bg-white/5 border-white/10 focus:border-purple-500' : 'bg-gray-50 border-gray-200 focus:border-purple-500'}`}
                    placeholder="React, AWS, AI..."
                />
            </div>
            <div className="space-y-2">
                <label className="text-[10px] font-black uppercase tracking-widest text-gray-500 flex items-center gap-2">
                    <Code className="w-3 h-3" /> Requested Technology
                </label>
                <input
                    required
                    name="requested_technology"
                    value={formData.requested_technology}
                    onChange={handleChange}
                    className={`w-full p-4 rounded-xl border outline-none transition-all ${isDark ? 'bg-white/5 border-white/10 focus:border-purple-500' : 'bg-gray-50 border-gray-200 focus:border-purple-500'}`}
                    placeholder="e.g. Docker, Rust, Go"
                />
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-[10px] font-black uppercase tracking-widest text-gray-500 flex items-center gap-2">
                <Star className="w-3 h-3" /> Experience Level
            </label>
            <select
                name="experience_level"
                value={formData.experience_level}
                onChange={handleChange}
                className={`w-full p-4 rounded-xl border outline-none transition-all appearance-none ${isDark ? 'bg-white/5 border-white/10 focus:border-purple-500' : 'bg-gray-50 border-gray-200 focus:border-purple-500'}`}
            >
                <option value="Beginner">Beginner</option>
                <option value="Intermediate">Intermediate</option>
                <option value="Advanced">Advanced</option>
                <option value="Expert">Expert</option>
            </select>
          </div>

          <div className="space-y-2">
            <label className="text-[10px] font-black uppercase tracking-widest text-gray-500 flex items-center gap-2">
                <MessageSquare className="w-3 h-3" /> Reason for Request
            </label>
            <textarea
                name="reason"
                value={formData.reason}
                onChange={handleChange}
                className={`w-full p-4 rounded-xl border outline-none transition-all min-h-[100px] ${isDark ? 'bg-white/5 border-white/10 focus:border-purple-500' : 'bg-gray-50 border-gray-200 focus:border-purple-500'}`}
                placeholder="Why do you want to learn this technology?"
            />
          </div>

          <button
            type="submit"
            disabled={submitting}
            className="w-full py-5 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-500 hover:to-blue-500 text-white font-black uppercase tracking-[0.2em] rounded-2xl shadow-xl shadow-purple-600/20 transition-all transform hover:scale-[1.01] active:scale-[0.99] disabled:opacity-50"
          >
            {submitting ? 'Submitting...' : 'Submit Suggestion'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default TechnologyVotingForm;
